import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader, useToast } from "@/components/design";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { createPayPeriod } from "./api";
import { usePayPeriodPermissions } from "./use-pay-period-permissions";

export function PayPeriodCreatePage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { canApprove } = usePayPeriodPermissions();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [autoCreateNext, setAutoCreateNext] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!canApprove) {
    return (
      <div className="space-y-4">
        <PageHeader title="Access denied" description="You do not have permission to create pay periods." />
        <Button variant="outline" onClick={() => navigate("/pay-periods")}>
          Back to pay periods
        </Button>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const res = await createPayPeriod({
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        autoCreateNext,
      });
      toast({
        variant: "success",
        title: "Pay period created",
        description: `${res.data.label} is ready for daily entry.`,
      });
      navigate(`/pay-periods/${res.data.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create pay period");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-xl space-y-6">
      <PageHeader
        title="New pay period"
        description="Leave dates blank to auto-calculate from your company's pay schedule."
      />

      <form onSubmit={handleSubmit} className="space-y-5 rounded-xl border bg-card-white p-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="startDate">Start date</Label>
            <Input
              id="startDate"
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); }}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="endDate">End date</Label>
            <Input
              id="endDate"
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); }}
            />
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={autoCreateNext}
            onChange={(e) => { setAutoCreateNext(e.target.checked); }}
            className="rounded border-input"
          />
          Auto-create next pay period
        </label>

        {error ? <p className="text-sm text-red-danger">{error}</p> : null}

        <div className="flex gap-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? "Creating…" : "Create period"}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate("/pay-periods")}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
